import { useState, useCallback } from 'react';
import {
  Stack,
  Button,
  Inline,
  InlineItem,
  LabelText,
  SegmentedControl,
  SegmentedControlItem,
  Text,
  Icon,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import {
  StyledTextarea,
  ProductPickerList,
  ProductPickerItem,
  ProductPickerLabel,
  ProductPickerName,
  ProductPickerDescription,
  ProductPickerCheck,
} from './feedback-form.styles';

const PRODUCTS = [
  {
    value: 'nzo',
    label: 'Kinesis for Organisations',
    description: 'Organisation-level sustainability reporting and benchmarking',
  },
  {
    value: 'community-decarbonisation',
    label: 'Kinesis for Communities',
    description: 'Community-scale decarbonisation planning',
  },
  {
    value: 'workspaces',
    label: 'Kinesis for Precincts',
    description: 'Precinct and building performance modelling',
  },
  {
    value: 'platform',
    label: 'Kinesis Platform, Authentication, API and Settings',
    description: 'Platform infrastructure, authentication, and configuration',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Something else not listed above',
  },
];

const CATEGORIES = [
  { value: 'feature-request', label: 'Feature Request' },
  { value: 'bug', label: 'Bug' },
  { value: 'general-feedback', label: 'General Feedback' },
];

const CATEGORY_HINTS: Record<string, string> = {
  'feature-request':
    'Focus on what you are trying to achieve, rather than how to build it.',
  bug: 'Be clear and specific. Links to the affected page or feature are helpful.',
  'general-feedback':
    "Tell us what's on your mind \u2014 what's working well, what's frustrating, or what could be better.",
};

const FeedbackForm = (props: FeedbackFormProps) => {
  const { defaultProduct, onSubmit = noop, isSubmitting = false } = props;

  const [product, setProduct] = useState<string>(defaultProduct ?? '');
  const [category, setCategory] = useState<string>('feature-request');
  const [description, setDescription] = useState('');

  const selectedProduct = PRODUCTS.find((p) => p.value === product);

  const canSubmit =
    product !== '' &&
    category !== '' &&
    description.trim() !== '' &&
    !isSubmitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({
      product,
      productLabel: selectedProduct?.label ?? product,
      category,
      description: description.trim(),
    });
  }, [canSubmit, product, selectedProduct, category, description, onSubmit]);

  return (
    <Stack space='large'>
      <Stack space='medium'>
        <Stack space='small'>
          <LabelText>Product</LabelText>
          <ProductPickerList>
            {PRODUCTS.map((p) => (
              <ProductPickerItem
                key={p.value}
                type='button'
                $selected={product === p.value}
                onClick={() => setProduct(p.value)}
              >
                <ProductPickerLabel>
                  <ProductPickerName>{p.label}</ProductPickerName>
                  <ProductPickerDescription>
                    {p.description}
                  </ProductPickerDescription>
                </ProductPickerLabel>
                <ProductPickerCheck>
                  {product === p.value && (
                    <Icon type='check-circle' magnitude='small' />
                  )}
                </ProductPickerCheck>
              </ProductPickerItem>
            ))}
          </ProductPickerList>
        </Stack>
        <Stack space='small'>
          <LabelText>Category</LabelText>
          <SegmentedControl
            value={category}
            onChange={(value: string | number) => setCategory(String(value))}
            widthMode='fill-container'
          >
            {CATEGORIES.map((c) => (
              <SegmentedControlItem key={c.value} value={c.value}>
                {c.label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        </Stack>
        <Stack space='small'>
          <LabelText>Description</LabelText>
          {CATEGORY_HINTS[category] && (
            <Text tone='secondary'>{CATEGORY_HINTS[category]}</Text>
          )}
          <StyledTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Describe your feedback...'
          />
        </Stack>
      </Stack>
      <Inline>
        <InlineItem sizing='fill-container' />
        <InlineItem sizing='fit-content'>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit feedback'}
          </Button>
        </InlineItem>
      </Inline>
    </Stack>
  );
};

export type FeedbackFormProps = {
  defaultProduct?: string;
  onSubmit?: (value: {
    product: string;
    productLabel: string;
    category: string;
    description: string;
  }) => void;
  isSubmitting?: boolean;
};

export { FeedbackForm, PRODUCTS };
